<template>
  <v-card-text>
    <!-- Header informativo -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-alert
          type="success"
          prominent
          border="start"
          class="mb-4"
        >
          <v-icon slot="prepend">mdi-plus-circle</v-icon>
          <div class="text-h6">Crear Nueva Incidencia</div>
          <div>Como supervisor, puedes crear incidencias para problemas detectados en tu área de supervisión. Completa todos los campos requeridos.</div>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Formulario de creación -->
    <v-form ref="formRef" v-model="formValid" @submit.prevent="submitIncident">
      <v-row>
        <!-- Información básica -->
        <v-col cols="12" md="8">
          <v-card variant="outlined" class="mb-4">
            <v-card-title>
              <v-icon left>mdi-information</v-icon>
              Información Básica
            </v-card-title>
            
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.title"
                    label="Título de la incidencia *"
                    variant="outlined"
                    :rules="titleRules"
                    required
                    placeholder="Ej: Problema con equipo de comunicación"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.description"
                    label="Descripción detallada *"
                    variant="outlined"
                    rows="4"
                    :rules="descriptionRules"
                    required
                    placeholder="Describe el problema, síntomas observados, y cualquier información relevante..."
                  />
                </v-col>

                <!--
                  P13-V1C: la CLASIFICACIÓN es el único canal canónico del enrutamiento del intake
                  (obligatoria; el servidor deriva el departamento destino de la política efectiva).
                  El catálogo es advisory — sólo clasificaciones con política efectiva para la
                  administración del actor; el backend re-resuelve y falla cerrado al confirmar.
                -->
                <v-col cols="12">
                  <v-select
                    v-model="form.classificationId"
                    :items="classificationOptions"
                    :loading="loadingClassifications"
                    item-title="name"
                    item-value="id"
                    label="Clasificación *"
                    variant="outlined"
                    :rules="classificationRules"
                    required
                    :no-data-text="loadingClassifications ? 'Cargando clasificaciones…' : 'Sin clasificaciones con política de enrutamiento efectiva'"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.category"
                    :items="categoryOptions"
                    label="Categoría *"
                    variant="outlined"
                    :rules="categoryRules"
                    required
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.priority"
                    :items="priorityOptions"
                    label="Prioridad *"
                    variant="outlined"
                    :rules="priorityRules"
                    required
                  >
                    <template v-slot:item="{ props, item }">
                      <v-list-item v-bind="props">
                        <template v-slot:prepend>
                          <v-icon :color="getPriorityColor(item.value)">
                            {{ getPriorityIcon(item.value) }}
                          </v-icon>
                        </template>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Panel lateral con ayuda -->
        <v-col cols="12" md="4">
          <v-card variant="outlined" class="mb-4">
            <v-card-title>
              <v-icon left color="info">mdi-help-circle</v-icon>
              Guía Rápida
            </v-card-title>
            
            <v-card-text>
              <div class="help-section">
                <h4 class="mb-2">Prioridades:</h4>
                <div class="d-flex align-center mb-1">
                  <v-icon color="red" size="16" class="me-2">mdi-alert</v-icon>
                  <span class="text-caption"><strong>Alta:</strong> Requiere atención inmediata</span>
                </div>
                <div class="d-flex align-center mb-1">
                  <v-icon color="orange" size="16" class="me-2">mdi-alert-outline</v-icon>
                  <span class="text-caption"><strong>Media:</strong> Resolver en 24-48h</span>
                </div>
                <div class="d-flex align-center mb-3">
                  <v-icon color="green" size="16" class="me-2">mdi-information</v-icon>
                  <span class="text-caption"><strong>Baja:</strong> Resolver cuando sea posible</span>
                </div>

                <h4 class="mb-2">Categorías más comunes:</h4>
                <v-chip-group column>
                  <v-chip
                    v-for="category in commonCategories"
                    :key="category"
                    size="small"
                    @click="form.category = category"
                    :color="form.category === category ? 'primary' : 'default'"
                  >
                    {{ category }}
                  </v-chip>
                </v-chip-group>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Grupo (filtro), Agencia y Departamento -->
        <v-col cols="12" md="8">
          <v-card variant="outlined" class="mb-4">
            <v-card-title>
              <v-icon left>mdi-store</v-icon>
              Grupo, Agencia y Departamento
            </v-card-title>

            <v-card-text>
              <v-alert
                v-if="agencyLoadError"
                type="error"
                variant="tonal"
                density="compact"
                class="mb-4"
                data-testid="agency-load-error"
              >
                {{ agencyLoadError }}
              </v-alert>
              <v-row>
                <!-- Filtro de Grupo: 'Todos' por defecto -->
                <v-col cols="12">
                  <v-select
                    v-model="groupFilter"
                    :items="groupFilterOptions"
                    label="Grupo (filtro)"
                    variant="outlined"
                    hint="Selecciona un grupo para filtrar las agencias. Con 'Todos' verás todas tus agencias."
                    persistent-hint
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="form.agencyId"
                    :items="filteredAgencyOptions"
                    label="Agencia (según grupo seleccionado) *"
                    variant="outlined"
                    :rules="agencyRules"
                    item-title="title"
                    item-value="value"
                    clearable
                    @update:model-value="handleAgencySelected"
                  >
                    <template #item="{ props, item }">
                      <v-list-item v-bind="props">
                        <v-list-item-title>{{ item.raw.title }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.raw.subtitle }}</v-list-item-subtitle>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!--
                  P13-K2-VERTICAL@V1 M3 (F1-B): el selector "Departamento operativo responsable *"
                  se retira de la creación. El departamento es ASIGNACIÓN y el backend no la acepta
                  en el cuerpo de creación; además era obligatorio, así que ningún reporte podía
                  enviarse. La asignación se realiza tras crear la incidencia (ver el aviso abajo).
                -->
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Ubicación -->
        <v-col cols="12" md="8">
          <v-card variant="outlined" class="mb-4">
            <v-card-title>
              <v-icon left>mdi-map-marker</v-icon>
              Ubicación
            </v-card-title>
            
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.location.address"
                    label="Dirección *"
                    variant="outlined"
                    :rules="addressRules"
                    required
                    placeholder="Ej: Calle Principal 123, Zona Centro"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.location.lat"
                    label="Latitud"
                    variant="outlined"
                    type="number"
                    step="any"
                    placeholder="Ej: -17.3895"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.location.lng"
                    label="Longitud"
                    variant="outlined"
                    type="number"
                    step="any"
                    placeholder="Ej: -66.1568"
                  />
                </v-col>

                <v-col cols="12">
                  <div class="d-flex gap-2">
                    <v-btn
                      @click="getCurrentLocation"
                      :loading="gettingLocation"
                      color="primary"
                      variant="outlined"
                    >
                      <v-icon left>mdi-crosshairs-gps</v-icon>
                      Usar Mi Ubicación
                    </v-btn>

                    <v-btn
                      @click="openMapPicker"
                      color="blue"
                      variant="outlined"
                    >
                      <v-icon left>mdi-map</v-icon>
                      Seleccionar en Mapa
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

  <!-- Asignación -->
        <v-col cols="12" md="8">
          <v-card variant="outlined" class="mb-4">
            <v-card-title>
              <v-icon left>mdi-account-circle</v-icon>
              Asignación
            </v-card-title>

            <v-card-text>
              <!--
                P13-K2-VERTICAL@V1 M3 (F1-B): los controles de departamento y responsable se retiran
                de la creación porque el cuerpo de creación no los admite. Se sustituyen por un aviso
                honesto en lugar de dejar campos inertes que sugieran una capacidad inexistente.
                Esto es INFORMACIÓN DE INTERFAZ, no un control de autorización: quien autoriza es el
                backend.
              -->
              <v-alert
                type="info"
                variant="tonal"
                density="comfortable"
                data-test="assignment-after-create-notice"
              >
                La incidencia se crea <strong>sin asignar</strong>. El departamento responsable y la
                persona asignada se establecen <strong>después de crearla</strong>, desde la propia
                incidencia, mediante la superficie de asignación autorizada.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Botones de acción -->
      <v-row class="mt-4">
        <v-col cols="12">
          <div class="d-flex justify-end gap-3">
            <v-btn
              @click="resetForm"
              color="grey"
              variant="outlined"
            >
              <v-icon left>mdi-refresh</v-icon>
              Limpiar
            </v-btn>

            <v-btn
              @click="saveDraft"
              color="orange"
              variant="outlined"
              :disabled="!form.title"
            >
              <v-icon left>mdi-content-save</v-icon>
              Guardar Borrador
            </v-btn>

            <v-btn
              type="submit"
              color="primary"
              :loading="submitting"
              :disabled="!formValid"
            >
              <v-icon left>mdi-plus-circle</v-icon>
              Crear Incidencia
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-form>

    <!-- Dialog de confirmación -->
    <v-dialog v-model="showConfirmDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <v-icon left color="success">mdi-check-circle</v-icon>
          Confirmar Creación
        </v-card-title>
        
        <v-card-text>
          <p>¿Estás seguro de que deseas crear esta incidencia?</p>
          
          <v-card variant="outlined" class="mt-3">
            <v-card-text>
              <div><strong>Título:</strong> {{ form.title }}</div>
              <div><strong>Prioridad:</strong> 
                <v-chip
                  :color="getPriorityColor(form.priority)"
                  size="small"
                  class="ml-1"
                >
                  {{ form.priority }}
                </v-chip>
              </div>
              <div><strong>Categoría:</strong> {{ form.category }}</div>
              <div><strong>Ubicación:</strong> {{ form.location.address }}</div>
            </v-card-text>
          </v-card>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showConfirmDialog = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            @click="confirmSubmit"
          >
            Crear Incidencia
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de selección de ubicación -->
    <v-dialog v-model="showMapDialog" max-width="800px">
      <v-card>
        <v-card-title>
          <v-icon left>mdi-map-marker-plus</v-icon>
          Seleccionar Ubicación
        </v-card-title>
        
        <v-card-text>
          <div class="map-placeholder">
            <div class="text-center py-8">
              <v-icon size="64" color="grey">mdi-map</v-icon>
              <div class="mt-2">Selector de ubicación en mapa</div>
              <div class="text-caption text-grey mt-2">
                Click en el mapa para seleccionar la ubicación
              </div>
              
              <!-- Controles de ubicación real -->
              <div class="mt-4">
                <v-text-field
                  v-model="tempLocation.address"
                  label="Dirección seleccionada"
                  variant="outlined"
                  readonly
                />
                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      v-model="tempLocation.lat"
                      label="Latitud"
                      variant="outlined"
                      readonly
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="tempLocation.lng"
                      label="Longitud"
                      variant="outlined"
                      readonly
                    />
                  </v-col>
                </v-row>
              </div>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showMapDialog = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            @click="confirmLocationSelection"
            :disabled="!tempLocation.lat || !tempLocation.lng"
          >
            Confirmar Ubicación
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card-text>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import SupervisorService from '@/services/supervisorService'
// P13-K2-VERTICAL@V1 M3 (F1-B): el store de autenticación ya no se consume aquí. Su único uso era
// componer `reportedBy` para el cuerpo de creación, y la procedencia la deriva el servidor.
import { ApiClient } from '@/services/api-client'
import { incidentsService } from '@/services/incidentsService'
import type { IncidentClassificationOption } from '@/types/incidents'

// ================== PROPS ==================
interface Props {
  supervisorId: number
}

const props = defineProps<Props>()

// ================== EMITS ==================
const emit = defineEmits<{
  'incident-created': []
}>()

// ================== ESTADO REACTIVO ==================
const formRef = ref()
const formValid = ref(false)
const submitting = ref(false)
const gettingLocation = ref(false)
const showConfirmDialog = ref(false)
const showMapDialog = ref(false)

// Formulario
const form = ref({
  title: '',
  description: '',
  category: '',
  priority: '',
  // P13-V1C: clasificación obligatoria del intake (catálogo del servidor).
  classificationId: null as number | null,
  location: {
    address: '',
    lat: null as number | null,
    lng: null as number | null
  },
  agencyId: null as number | null,
  pointId: null as number | null,
  groupId: null as number | null
  // P13-K2-VERTICAL@V1 M3 (F1-B): `departmentId` y `assignedTo` se retiran del estado del
  // formulario. Eran ASIGNACIÓN, no contenido, y el cuerpo de creación no la admite; conservarlos
  // sólo dejaría un camino por el que pudieran volver a la petición.
})

// Ubicación temporal para selector de mapa
const tempLocation = ref({
  address: '',
  lat: null as number | null,
  lng: null as number | null
})

// Opciones para los selectores
const categoryOptions = [
  'Infraestructura',
  'Comunicaciones',
  'Seguridad',
  'Mantenimiento',
  'Personal',
  'Equipamiento',
  'Transporte',
  'Servicios',
  'Otros'
]

const priorityOptions = [
  { title: 'Alta', value: 'alta' },
  { title: 'Media', value: 'media' },
  { title: 'Baja', value: 'baja' }
]

const commonCategories = [
  'Infraestructura',
  'Comunicaciones',
  'Seguridad',
  'Mantenimiento'
]

// P13-V1C: catálogo advisory de clasificaciones (sólo las que tienen política efectiva).
const classificationOptions = ref<IncidentClassificationOption[]>([])
const loadingClassifications = ref(false)

const groupOptions = ref<Array<{title: string, value: number}>>([])
// Filtro de grupo (UI) con opción 'Todos'. P13 W2 · XSC-M1: es SÓLO un estrechamiento visual de la
// lista de agencias; nunca decide el `groupId` que viaja en el cuerpo de creación.
const groupFilter = ref<number | 'all'>('all')
const groupFilterOptions = computed(() => {
  return [{ title: 'Todos', value: 'all' as const }, ...groupOptions.value]
})
// Cada opción lleva el grupo CANÓNICO de la agencia (el `groupId` de la propia fila del punto,
// verificado contra su grupo contenedor por el adaptador del servicio).
const agencyOptions = ref<Array<{ title: string; value: number; subtitle?: string; groupId: number; groupName: string }>>([])
// Estado de error NOMBRADO del lector de agencias: un fallo (denegación, forma imposible) se muestra;
// nunca se degrada en silencio a un selector vacío.
const agencyLoadError = ref<string | null>(null)
const filteredAgencyOptions = computed(() => {
  if (groupFilter.value === 'all') return agencyOptions.value
  return agencyOptions.value.filter(a => a.groupId === groupFilter.value)
})

// Reglas de validación
const titleRules = [
  (v: string) => !!v || 'El título es requerido',
  (v: string) => (v && v.length >= 10) || 'El título debe tener al menos 10 caracteres',
  (v: string) => (v && v.length <= 100) || 'El título no puede exceder 100 caracteres'
]

const descriptionRules = [
  (v: string) => !!v || 'La descripción es requerida',
  (v: string) => (v && v.length >= 20) || 'La descripción debe tener al menos 20 caracteres',
  (v: string) => (v && v.length <= 1000) || 'La descripción no puede exceder 1000 caracteres'
]

const categoryRules = [
  (v: string) => !!v || 'La categoría es requerida'
]

const priorityRules = [
  (v: string) => !!v || 'La prioridad es requerida'
]

// P13-V1C: sin clasificación el backend responde 400 CLASSIFICATION_REQUIRED — se exige aquí para
// que el fallo sea visible antes de enviar, pero la autoridad es siempre del servidor.
const classificationRules = [
  (v: number | null) => !!v || 'La clasificación es requerida'
]

const addressRules = [
  (v: string) => !!v || 'La dirección es requerida',
  (v: string) => (v && v.length >= 10) || 'La dirección debe tener al menos 10 caracteres'
]

const groupRules = [
  (v: number) => !!v || 'Debe seleccionar un grupo responsable'
]

const agencyRules = [
  (v: number) => !!v || 'Debe seleccionar una agencia'
]

// P13-K2-VERTICAL@V1 M3 (F1-B): `departmentRules` se elimina — el departamento ya no es un campo de
// creación, así que exigirlo bloqueaba el envío sin aportar ninguna autoridad.

// ================== COMPUTED ==================
const hasCoordinates = computed(() => {
  return form.value.location.lat !== null && form.value.location.lng !== null
})

// ================== MÉTODOS ==================
/**
 * P13 W2 · XSC-M1 — grupos y agencias SUPERVISADOS para el selector del intake firmado.
 *
 * El lector es el corredor canónico `GET /groups/with-agencies` (partición del servidor derivada
 * del MISMO resolver que autoriza `POST /field-supervisor/incidents`), consumido a través del
 * adaptador estricto `SupervisorService.getSupervisedGroupsWithAgencies()`, que respeta el contrato
 * real del cliente (una sola desencapsulación) y verifica la relación agencia→grupo.
 *
 * La ruta anterior, `/field-supervisor/assigned-agencies`, está contenida a vendor-only (#214) y
 * NO se consulta: ni como fuente ni como fallback. Un fallo del lector es un estado VISIBLE
 * (`agencyLoadError`), nunca un selector vacío en silencio.
 */
const describeAgencyLoadError = (error: any): string => {
  const status = error?.response?.status ?? error?.status
  const message = String(error?.message || '')
  if (status === 403) return 'No fue posible cargar las agencias supervisadas: acceso denegado por el servidor.'
  if (message.startsWith('SUPERVISED_')) return `No fue posible interpretar las agencias supervisadas (${message.split(':')[0]}).`
  return 'No fue posible cargar las agencias supervisadas. Recarga la página o contacta al administrador.'
}

const loadAgencies = async () => {
  agencyLoadError.value = null
  try {
    const { groups, agencies } = await SupervisorService.getSupervisedGroupsWithAgencies()
    groupOptions.value = groups.map(g => ({ title: g.groupName, value: g.groupId }))
    agencyOptions.value = agencies.map(a => ({
      title: a.agencyName,
      value: a.agencyId,
      subtitle: a.address ?? undefined,
      groupId: a.groupId,
      groupName: a.groupName
    }))
  } catch (error) {
    console.error('Error loading supervised agencies:', error)
    agencyOptions.value = []
    groupOptions.value = []
    agencyLoadError.value = describeAgencyLoadError(error)
  }
}

// P13-K2-VERTICAL@V1 M3 (F1-B): `loadOperationalDepartments` se elimina con el selector que lo
// alimentaba — el formulario de creación ya no consulta `/departments`.

/**
 * P13-V1C: carga el catálogo advisory de clasificaciones. El supervisor de campo tiene una única
 * administración resoluble por scope, así que no se envía contexto adicional.
 */
const loadClassifications = async () => {
  loadingClassifications.value = true
  try {
    classificationOptions.value = await incidentsService.getClassifications()
  } catch (error) {
    console.error('Error loading classifications:', error)
    classificationOptions.value = []
  } finally {
    loadingClassifications.value = false
  }
}

/**
 * Obtiene la ubicación actual del usuario
 */
const getCurrentLocation = () => {
  if (!navigator.geolocation) {
    alert('La geolocalización no está soportada por este navegador.')
    return
  }

  gettingLocation.value = true
  
  navigator.geolocation.getCurrentPosition(
    (position) => {
      form.value.location.lat = position.coords.latitude
      form.value.location.lng = position.coords.longitude
      
      form.value.location.address = `Ubicación actual (${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)})`
      
      gettingLocation.value = false
    },
    (error) => {
      console.error('Error obteniendo ubicación:', error)
      alert('No se pudo obtener la ubicación actual.')
      gettingLocation.value = false
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 600000
    }
  )
}

/**
 * Abre el selector de mapa
 */
const openMapPicker = () => {
  tempLocation.value = {
    address: form.value.location.address,
    lat: form.value.location.lat,
    lng: form.value.location.lng
  }
  
  showMapDialog.value = true
}

/**
 * Confirma la selección de ubicación del mapa
 */
const confirmLocationSelection = () => {
  form.value.location = { ...tempLocation.value }
  showMapDialog.value = false
}

// Cuando se selecciona una agencia: fijar pointId y el groupId CANÓNICO de esa agencia.
// P13 W2 · XSC-M1 — VINCULACIÓN EXACTA: el grupo que viaja en el cuerpo es el grupo propietario de
// la agencia según el servidor (la opción lo trae del lector canónico). El filtro de grupo, la
// posición en la lista o el texto mostrado NUNCA lo deciden. Una agencia fuera de las opciones
// (p. ej. inyectada) no vincula nada: el hard-guard de `confirmSubmit` detiene el envío y el
// servidor sigue siendo la autoridad que deniega.
const handleAgencySelected = (agencyId: number | null) => {
  form.value.agencyId = agencyId
  const found = agencyId ? agencyOptions.value.find(a => a.value === agencyId) : undefined
  if (!found) {
    form.value.agencyId = null
    form.value.pointId = null
    form.value.groupId = null
    return
  }
  form.value.pointId = found.value
  form.value.groupId = found.groupId
  if (found.subtitle) {
    form.value.location.address = found.subtitle
  }
}

/**
 * Envía el formulario
 */
const submitIncident = async () => {
  const validation = await formRef.value?.validate()
  if (!validation.valid) return
  
  showConfirmDialog.value = true
}

/**
 * Confirma y envía la incidencia
 */
const confirmSubmit = async () => {
  try {
    submitting.value = true
    showConfirmDialog.value = false
    
    const mapPriority = (p: string) => {
      const v = (p || '').toLowerCase()
      if (v === 'alta' || v === 'high') return 'high'
      if (v === 'baja' || v === 'low') return 'low'
      if (v === 'critica' || v === 'crítica' || v === 'critical') return 'critical'
      return 'medium'
    }

    // P13-K2-VERTICAL@V1 M3 (F1-B) — EL CUERPO DE CREACIÓN NO LLEVA AUTORIDAD.
    //
    // El backend ratificó (PR #150, head c8603962…) que la procedencia, la asignación y el estado
    // NO se envían al crear: `reportedBy` lo deriva el servidor del actor persistido, una incidencia
    // nueva nace SIEMPRE sin asignar y su estado es `pending` derivado por el servidor. Enviarlos se
    // deniega con un 403 estable (INTAKE_BODY_REPORTED_BY_AS_AUTHORITY /
    // INTAKE_BODY_ASSIGNEE_AS_AUTHORITY / INTAKE_BODY_ASSIGNED_DEPARTMENT_AS_AUTHORITY /
    // INTAKE_BODY_STATUS_AS_AUTHORITY), incluso con el valor correcto — el cuerpo no es canal de
    // autoridad, así que "correcto" no es defensa. Este formulario enviaba `reportedBy` siempre y
    // exigía `assignedDepartmentId`, de modo que toda creación quedaba rota contra el contrato.
    //
    // El cuerpo queda con CONTENIDO (título, descripción, prioridad) y el DESTINO solicitado
    // (grupo/punto/agencia), que no es autoridad: el kernel lo prueba contra la partición canónica
    // del supervisor. La asignación de departamento/responsable se hace DESPUÉS de crear, por la
    // superficie de actualización independientemente autorizada, que este formulario no toca.
    // P13-V1C: `classificationId` es CONTENIDO de intake obligatorio (no autoridad): el servidor
    // deriva de él la política de enrutamiento y el departamento destino. Ningún alias viaja.
    const payload: any = {
      title: form.value.title,
      description: form.value.description,
      priority: mapPriority(form.value.priority),
      classificationId: form.value.classificationId,
      groupId: form.value.groupId,
      pointId: form.value.pointId,
      agencyId: form.value.pointId
    }

    if (!payload.groupId || !payload.pointId) {
      alert('Debe seleccionar una agencia válida (define grupo y punto).')
      submitting.value = false
      return
    }

    if (!payload.classificationId) {
      alert('Debe seleccionar la clasificación de la incidencia.')
      submitting.value = false
      return
    }

    await ApiClient.post('/field-supervisor/incidents', payload)
    
    // Limpiar formulario y emitir evento
    resetForm()
    emit('incident-created')
    
  } catch (error) {
    console.error('Error creating incident:', error)
    const status = (error as any)?.response?.status || (error as any)?.status
    const code = (error as any)?.response?.data?.code || (error as any)?.code
    if (status === 403 || code === 'FIELD_SUPERVISOR_SCOPE_DENIED') {
      alert((error as any)?.response?.data?.message || 'Acceso denegado para crear la incidencia en esa agencia.')
    } else if (status === 501 || code === 'NOT_IMPLEMENTED_YET') {
      alert((error as any)?.response?.data?.message || 'Creación de incidencias pendiente de implementación backend scoped.')
    } else {
      alert('Error al crear la incidencia. Por favor, inténtalo de nuevo.')
    }
  } finally {
    submitting.value = false
  }
}

/**
 * Guarda como borrador
 */
const saveDraft = () => {
  const draft = JSON.stringify(form.value)
  localStorage.setItem(`incident_draft_${props.supervisorId}`, draft)
  alert('Borrador guardado exitosamente')
}

/**
 * Carga borrador guardado.
 * P13-V1C repair R1: el borrador se RECONCILIA contra el estado actual — un borrador de forma
 * antigua (sin `classificationId`) recibe la clave en null, y una clasificación guardada que ya no
 * existe en el catálogo vigente se anula (misma disciplina que las otras superficies con catálogo).
 * La detención sigue siendo información de interfaz: la autoridad es siempre del servidor.
 */
const loadDraft = () => {
  const draft = localStorage.getItem(`incident_draft_${props.supervisorId}`)
  if (draft) {
    try {
      const parsed = JSON.parse(draft)
      form.value = { classificationId: null, ...parsed }
      if (form.value.classificationId && classificationOptions.value.length
          && !classificationOptions.value.some(c => c.id === form.value.classificationId)) {
        form.value.classificationId = null
      }
    } catch (error) {
      console.error('Error loading draft:', error)
    }
  }
}

// P13-V1C repair R1: el catálogo llega DESPUÉS del borrador (ambos en onMounted); al resolverse se
// re-aplica la poda para que una clasificación retirada no sobreviva por orden de carga.
watch(classificationOptions, (options) => {
  if (form.value.classificationId && options.length && !options.some(c => c.id === form.value.classificationId)) {
    form.value.classificationId = null
  }
})

/**
 * Resetea el formulario
 */
const resetForm = () => {
  form.value = {
    title: '',
    description: '',
    category: '',
    priority: '',
    classificationId: null,
    location: {
      address: '',
      lat: null,
      lng: null
    },
  agencyId: null,
  pointId: null,
    groupId: null
  }
  
  // Limpiar borrador
  localStorage.removeItem(`incident_draft_${props.supervisorId}`)
  
  // Resetear validación
  formRef.value?.resetValidation()
}

/**
 * Obtiene el color según la prioridad
 */
const getPriorityColor = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'alta': return 'red'
    case 'media': return 'orange'
    case 'baja': return 'green'
    default: return 'grey'
  }
}

/**
 * Obtiene el ícono según la prioridad
 */
const getPriorityIcon = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'alta': return 'mdi-alert'
    case 'media': return 'mdi-alert-outline'
    case 'baja': return 'mdi-information'
    default: return 'mdi-help-circle'
  }
}

// ================== CICLO DE VIDA ==================
onMounted(() => {
  // Grupos y agencias llegan juntos desde el corredor canónico (no se derivan unos de otras).
  loadAgencies()
  loadClassifications()
  loadDraft()
})

// Cuando cambia el filtro de grupo, limpiar la agencia seleccionada y su destino derivado.
// P13 W2 · XSC-M1: el filtro NO fija `groupId`; sólo una agencia seleccionada lo vincula.
watch(groupFilter, () => {
  form.value.agencyId = null
  form.value.pointId = null
  form.value.groupId = null
})

// P13-K2-VERTICAL@V1 M3 (F1-B): el watcher de depuración de `departmentId` desaparece con el campo.

// ================== EXPOSICIÓN ==================
defineExpose({
  resetForm,
  loadDraft
})
</script>

<style scoped>
.help-section {
  font-size: 0.875rem;
}

.map-placeholder {
  height: 400px;
  background-color: #f5f5f5;
  border-radius: 8px;
  border: 2px dashed #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.v-card.v-card--variant-outlined {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.v-chip-group {
  margin-top: 8px;
}

.text-caption {
  line-height: 1.4;
}
</style>
